<template>
  <v-dialog
    v-model="show"
    persistent
    width="90vw"
    scrollable
    :fullscreen="$vuetify.breakpoint.mobile"
  >
    <template
      v-for="(_, scopedSlotName) in $scopedSlots"
      #[scopedSlotName]="slotData"
    >
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>
    <template v-for="(_, slotName) in $slots" #[slotName]>
      <slot :name="slotName" />
    </template>
    <v-card :loading="isLoading">
      <v-card-title>
        <div v-if="isAdd">Add Study Progress</div>
        <div v-else>Edit Study Progress</div>
        <v-spacer></v-spacer>
        <v-btn icon @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-form ref="form">
          <v-list flat>
            <template v-for="(progressDetail, i) in form.details">
              <v-list-item :key="i">
                <v-list-item-content>
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" md="3">
                        <div class="label mb-2">Lesson</div>
                        <v-text-field
                          v-model="progressDetail.lesson_name"
                          :rules="[validationRules.required]"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Input lesson name"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="label mb-2">Category</div>
                        <v-autocomplete
                          v-model="progressDetail.lesson_category_id"
                          :items="categories"
                          :item-text="
                            (item) => bilingualText(item, 'name', 'name_cn')
                          "
                          item-value="id"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Choose category"
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <div class="label mb-2">Score</div>
                        <v-select
                          v-model="progressDetail.score_num"
                          :items="scoreGradeOptions"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Choose score"
                          @change="changeScore(progressDetail, $event)"
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <div class="label mb-2">Notes</div>
                        <v-textarea
                          v-model="progressDetail.notes"
                          solo
                          flat
                          outlined
                          dense
                          auto-grow
                          rows="1"
                          placeholder="Additional notes"
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <div class="label mb-2">Notes (Chinese)</div>
                        <v-textarea
                          v-model="progressDetail.notes_cn"
                          solo
                          flat
                          outlined
                          dense
                          auto-grow
                          rows="1"
                          placeholder="Additional notes (Chinese)"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon color="error" @click="removeDetail(i)">
                    <v-icon>mdi-delete-forever</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider
                v-if="i < form.details.length - 1"
                :key="`div-${i}`"
              ></v-divider>
            </template>
            <v-list-item>
              <v-btn depressed color="success" @click="addDetail">
                <v-icon left>mdi-plus-circle</v-icon> Add Score
              </v-btn>
            </v-list-item>
          </v-list>
          <div v-if="isLastSession">
            <v-card-subtitle>
              Please input a summary notes at the last class session/meeting of
              each month
            </v-card-subtitle>
            <div class="label mb-2">Summary</div>
            <v-textarea
              v-model="form.notes"
              :rules="[validationRules.required]"
              solo
              flat
              outlined
              dense
              auto-grow
              placeholder="Study progress summary"
            />
            <div class="label mb-2">Summary (Chinese)</div>
            <v-textarea
              v-model="form.notes_cn"
              solo
              flat
              outlined
              dense
              auto-grow
              placeholder="Study progress summary (Chinese)"
            />
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-container fluid>
          <v-row justify="end">
            <v-col md="3" cols="12">
              <v-btn block depressed outlined @click="show = false">
                Cancel
              </v-btn>
            </v-col>
            <v-col md="3" cols="12">
              <v-btn
                block
                depressed
                color="primary"
                :disabled="isLoading"
                @click="submit"
              >
                Save Changes
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import constant from '@/constant'
export default {
  name: 'StudyProgressDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    lessons: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    submitApi: {
      type: Boolean,
      default: false,
    },
    isLastSession: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        student_id: null,
        classroom_schedule_id: null,
        notes: null,
        notes_cn: null,
        details: [],
      },
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'studyProgress/isDetailLoading',
      progress: 'studyProgress/detail',
    }),
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    isAdd() {
      return !this.item.id
    },
    scoreGradeOptions() {
      return constant.scoreGradeOptions
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      createProgress: 'studyProgress/create',
      updateProgress: 'studyProgress/update',
    }),
    init() {
      _.merge(this.form, this.item)
      if (this.isAdd) {
        for (const lesson of this.lessons) {
          this.form.details.push({
            lesson_id: lesson.id,
            lesson_name: lesson.name,
            lesson_name_cn: lesson.name_cn,
            lesson_category_id: lesson.lesson_category_id,
            score_num: 0,
            score_grade: this.scoreGradeProps(0, 'text'),
            notes: null,
            notes_cn: null,
          })
        }
      }
    },
    changeScore(item, value) {
      item.score_grade = this.scoreGradeProps(value, 'text')
    },
    addDetail() {
      this.form.details.push({
        lesson_id: null,
        lesson_name: null,
        lesson_name_cn: null,
        score_num: 0,
        score_grade: this.scoreGradeProps(0, 'text'),
        notes: null,
        notes_cn: null,
      })
    },
    removeDetail(index) {
      this.form.details.splice(index, 1)
    },
    resetForm() {
      this.form = {
        student_id: null,
        classroom_schedule_id: null,
        notes: null,
        notes_cn: null,
        details: [],
      }
    },
    async submit() {
      if (this.$refs.form.validate()) {
        const req = JSON.parse(JSON.stringify(this.form))
        let err = null
        if (this.submitApi) {
          if (this.isAdd) {
            err = await this.createProgress(req)
          } else {
            err = await this.updateProgress(req)
          }
        }
        if (err == null) {
          const resp = JSON.parse(JSON.stringify(this.progress))
          this.$emit('submit', resp)
          this.$emit('update:item', resp)
          this.resetForm()
        }
      }
    },
  },
}
</script>
